import { z } from "zod";

const Email = z.string().email().max(254);
const ModuleEnum = z.enum(["procedure", "shifts", "monitoring", "reservations", "automation"]);

/**
 * Every rule on the checklist-execution tree is gated on monitoring being active
 * (firestore.rules). A tenant holding `reservations` without `monitoring` would,
 * from S5 on, generate derived housekeeping work that no client can read — a
 * silent failure. `activeModules` is superadmin-only, so this is the one place
 * the combination can be refused.
 */
const monitoringSatisfied = (m: ReadonlyArray<string> | undefined): boolean => {
  // Both modules put work into Monitoring's tasks: reservations mirrors the
  // stays that trigger it, automation decides what to create. Either one without
  // monitoring would generate work no client can read.
  const needs = m?.includes("reservations") === true || m?.includes("automation") === true;
  return !needs || m?.includes("monitoring") === true;
};

const RESERVATIONS_NEEDS_MONITORING = {
  message: "reservations and automation require monitoring",
  // Not `as const`: zod 4 wants a mutable PropertyKey[] here.
  path: ["activeModules"] as PropertyKey[],
};

// Identity Platform tenant slug constraint mirrored from
// `functions/scripts/bootstrap-mademind.mjs`. Keep in sync.
const TenantSlug = z
  .string()
  .min(4)
  .max(20)
  .regex(/^[a-z][a-z0-9-]+$/, "lowercase, start with letter, [a-z0-9-]");

export const CreateTenantSchema = z
  .object({
    displayName: TenantSlug,
    ownerEmail: Email,
    ownerDisplayName: z.string().min(1).max(120),
    billingEmail: Email.optional(),
    activeModules: z.array(ModuleEnum).max(10).optional(),
  })
  .strict()
  .refine((d) => monitoringSatisfied(d.activeModules), RESERVATIONS_NEEDS_MONITORING);
export type CreateTenantInput = z.infer<typeof CreateTenantSchema>;

export const PatchTenantSchema = z
  .object({
    displayName: z.string().min(1).max(120),
    billingEmail: Email,
    activeModules: z.array(ModuleEnum).max(10),
  })
  .partial()
  .strict()
  .refine((d) => monitoringSatisfied(d.activeModules), RESERVATIONS_NEEDS_MONITORING);
export type PatchTenantInput = z.infer<typeof PatchTenantSchema>;

export const DisableTenantSchema = z
  .object({
    reason: z.string().min(3).max(500),
  })
  .strict();
export type DisableTenantInput = z.infer<typeof DisableTenantSchema>;

export const OwnerRelinkSchema = z
  .object({
    ownerEmail: Email,
  })
  .strict();
export type OwnerRelinkInput = z.infer<typeof OwnerRelinkSchema>;

export const InviteOperatorSchema = z
  .object({
    email: Email,
    displayName: z.string().min(1).max(120),
  })
  .strict();
export type InviteOperatorInput = z.infer<typeof InviteOperatorSchema>;

export const ImpersonateSchema = z
  .object({
    targetTenantId: z.string().min(1).max(64),
    targetUid: z.string().min(1).max(128),
    reason: z.string().min(3).max(500),
  })
  .strict();
export type ImpersonateInput = z.infer<typeof ImpersonateSchema>;

export const EndImpersonationSchema = z
  .object({
    targetTenantId: z.string().min(1).max(64),
    targetUid: z.string().min(1).max(128),
  })
  .strict();
export type EndImpersonationInput = z.infer<typeof EndImpersonationSchema>;

export const SystemTemplateSchema = z
  .object({
    category: z.enum(["frontoffice", "housekeeping", "kitchen", "haccp", "maintenance", "generic"]),
    title: z.object({
      it: z.string().min(1).max(120),
      en: z.string().min(1).max(120),
    }),
    description: z.object({
      it: z.string().min(1).max(500),
      en: z.string().min(1).max(500),
    }),
    contentMarkdown: z.string().min(20).max(20000),
    suggestedDepartment: z.string().max(60).optional(),
  })
  .strict();
export type SystemTemplateInput = z.infer<typeof SystemTemplateSchema>;

export const PatchSystemTemplateSchema = SystemTemplateSchema.partial();
export type PatchSystemTemplateInput = z.infer<typeof PatchSystemTemplateSchema>;

export const PatchEnabledIntegrationsSchema = z
  .object({
    enabledIntegrations: z.array(z.string().min(1)),
  })
  .strict();
export type PatchEnabledIntegrationsInput = z.infer<typeof PatchEnabledIntegrationsSchema>;
